import React, {useEffect, useRef, useState} from 'react';
import {Modal, Pressable, ScrollView, TextInput, View, useWindowDimensions} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {
  ArchiveReason,
  ArchiveRisk,
  CONFIRM_WORD,
  deleteLabel,
  dialogBody,
  dialogTitle,
  riskText,
} from '../courseArchive';
import {F, S, STATUS, T} from '../courseTheme';
import {E} from '../editor/editorTheme';
import {ring} from '../editor/ui';
import {ListButton} from './CourseListControls';

type Props = {
  /** How many courses the Delete will archive. */
  count: number;
  /** Published or still-building courses among them, with why, in order. */
  risks: ArchiveRisk[];
  /** Ask for DELETE to be typed. True whenever any risk is known, or the server said so. */
  typed: boolean;
  busy: boolean;
  /** Already in our words. */
  error: string | null;
  onConfirm: (confirm?: typeof CONFIRM_WORD) => void;
  onCancel: () => void;
  reduced: boolean;
};

const RISK_ICON: Record<ArchiveReason, string> = {
  published: 'people-outline',
  building: 'construct-outline',
  other: 'help-circle-outline',
};

function closerLook(count: number, risky: number): string {
  if (risky === 0) return 'Some of these need a typed confirm before they go.';
  if (count === 1) return 'Before it goes:';
  return risky === 1
    ? '1 of these needs a closer look:'
    : `${risky} of these need a closer look:`;
}

/**
 * "Delete N courses?" — which archives them, and says so.
 *
 * Plain when nothing selected is published or still building: one sentence
 * and two buttons. Otherwise it names each of those courses with what
 * deleting does to it, in words ("Published — students will lose access"),
 * and Delete only enables once the field reads exactly DELETE. When the
 * server is the one that knows (a 409), the dialog switches to that form in
 * place and moves focus to the field.
 *
 * A react-native Modal, i.e. a portal on the web, which brings role=dialog,
 * aria-modal, a focus trap and Escape. Focus starts on Cancel — the safe
 * choice — or on the field when one is asked for, and returns to whatever
 * opened the dialog when it closes.
 */
export default function ArchiveConfirmDialog({
  count,
  risks,
  typed,
  busy,
  error,
  onConfirm,
  onCancel,
  reduced,
}: Props) {
  const [value, setValue] = useState('');
  const [fieldFocused, setFieldFocused] = useState(false);
  const {width, height} = useWindowDimensions();
  const cancelRef = useRef<any>(null);
  const fieldRef = useRef<any>(null);

  // Back to the opener on close.
  useEffect(() => {
    const doc = (globalThis as any).document;
    const opener = doc?.activeElement ?? null;
    return () => {
      if (opener && doc?.contains?.(opener)) opener.focus?.();
    };
  }, []);

  // On open, and again when a 409 turns this into the typed form.
  useEffect(() => {
    (typed ? fieldRef : cancelRef).current?.focus?.();
  }, [typed]);

  const exact = value === CONFIRM_WORD;
  // Close but not exact — lower case, a stray space. Said, not just refused.
  const nearly =
    !!value && !exact && value.trim().toUpperCase() === CONFIRM_WORD;
  const canConfirm = !busy && (!typed || exact);
  const title = dialogTitle(count);
  const confirm = () => {
    if (canConfirm) onConfirm(typed ? CONFIRM_WORD : undefined);
  };
  const cancel = () => {
    if (!busy) onCancel();
  };

  const panelWidth = Math.max(280, Math.min(480, width - 32));

  return (
    <Modal
      visible
      transparent
      animationType={reduced ? 'none' : 'fade'}
      onRequestClose={cancel}
      {...({'aria-label': title} as any)}>
      <View style={{flex: 1, alignItems: 'center', justifyContent: 'center', padding: S[4]}}>
        {/* Clicking away cancels. Not a second control for screen readers:
            they have Cancel and Escape. */}
        <Pressable
          accessible={false}
          focusable={false}
          onPress={cancel}
          style={
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: E.scrim,
              cursor: 'default',
            } as any
          }
        />

        <View
          style={{
            width: panelWidth,
            maxHeight: Math.max(320, height - 32),
            backgroundColor: F.white,
            borderRadius: F.radius.card,
            overflow: 'hidden',
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 12},
            shadowOpacity: 0.18,
            shadowRadius: 32,
            elevation: 12,
          }}>
          <ScrollView
            style={{flexGrow: 0, flexShrink: 1}}
            contentContainerStyle={{padding: S[6], gap: S[4]}}>
            <View style={{flexDirection: 'row', gap: S[3], alignItems: 'flex-start'}}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: STATUS.bad.bg,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Ionicons name="trash-outline" size={20} color={STATUS.bad.fg} />
              </View>
              <View style={{flex: 1, gap: S[1], paddingTop: 2}}>
                <CCText
                  accessibilityRole="header"
                  style={{fontSize: T.xl, fontWeight: '700', color: F.brownDark}}>
                  {title}
                </CCText>
                <CCText style={{fontSize: T.base, fontWeight: '500', color: F.brown, lineHeight: 22}}>
                  {dialogBody(count)}
                </CCText>
              </View>
            </View>

            {typed ? (
              <View style={{gap: S[3]}}>
                <View
                  accessibilityLiveRegion="polite"
                  style={{flexDirection: 'row', alignItems: 'flex-start', gap: S[2]}}>
                  <Ionicons name={STATUS.warn.icon} size={18} color={STATUS.warn.fg} />
                  <CCText
                    style={{flex: 1, fontSize: T.base, fontWeight: '600', color: F.brownDark, lineHeight: 22}}>
                    {closerLook(count, risks.length)}
                  </CCText>
                </View>

                {risks.length ? (
                  <View
                    style={{
                      borderWidth: 1,
                      borderColor: E.line,
                      borderRadius: 10,
                      paddingHorizontal: S[3],
                    }}>
                    {risks.map((risk, i) => (
                      <View
                        key={risk.video_course_id}
                        accessible
                        accessibilityLabel={`${risk.title}: ${riskText(risk.reason)}`}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: S[2],
                          paddingVertical: S[3],
                          borderTopWidth: i === 0 ? 0 : 1,
                          borderTopColor: E.line,
                        }}>
                        <Ionicons
                          name={RISK_ICON[risk.reason]}
                          size={18}
                          color={STATUS.warn.fg}
                          style={{marginTop: 2}}
                        />
                        <View style={{flex: 1, gap: 2}}>
                          <CCText
                            numberOfLines={2}
                            style={{fontSize: T.base, fontWeight: '600', color: F.brownDark}}>
                            {risk.title}
                          </CCText>
                          {/* STATUS.warn.fg on white: 5.02:1. */}
                          <CCText style={{fontSize: T.sm, fontWeight: '600', color: STATUS.warn.fg}}>
                            {riskText(risk.reason)}
                          </CCText>
                        </View>
                      </View>
                    ))}
                  </View>
                ) : null}

                <View style={{gap: S[1]}}>
                  <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: F.brownDark}}>
                    {`Type ${CONFIRM_WORD} to confirm`}
                  </CCText>
                  {/* The ring is on a wrapper: TextInput's style types reject the
                      web-only outline keys. */}
                  <View style={[{borderRadius: F.radius.button}, ring(fieldFocused)]}>
                    <TextInput
                      ref={fieldRef}
                      value={value}
                      onChangeText={setValue}
                      accessibilityLabel="Type DELETE to confirm"
                      autoCapitalize="characters"
                      autoCorrect={false}
                      autoComplete="off"
                      spellCheck={false}
                      editable={!busy}
                      returnKeyType="done"
                      onSubmitEditing={confirm}
                      // A field shows focus however it was reached: it is where typing goes.
                      onFocus={() => setFieldFocused(true)}
                      onBlur={() => setFieldFocused(false)}
                      style={
                        {
                          minHeight: 44,
                          borderWidth: 1,
                          // 3.57:1 against white idle, tealDark 5.23:1 focused.
                          borderColor: fieldFocused ? F.tealDark : E.inputLine,
                          borderRadius: F.radius.button,
                          paddingHorizontal: S[3],
                          // 16px so a phone does not zoom the page on focus.
                          fontSize: 16,
                          fontWeight: '600',
                          letterSpacing: 1,
                          color: F.brownDark,
                          backgroundColor: F.white,
                        } as any
                      }
                    />
                  </View>
                  {nearly ? (
                    <CCText
                      accessibilityLiveRegion="polite"
                      style={{fontSize: T.sm, fontWeight: '500', color: F.brown}}>
                      Type it exactly: DELETE, in capitals, with no spaces.
                    </CCText>
                  ) : null}
                </View>
              </View>
            ) : null}

            {error ? (
              <View
                accessibilityLiveRegion="polite"
                {...({role: 'alert'} as any)}
                style={{flexDirection: 'row', alignItems: 'flex-start', gap: S[2]}}>
                <Ionicons name={STATUS.bad.icon} size={18} color={STATUS.bad.fg} />
                <CCText
                  style={{flex: 1, fontSize: T.base, fontWeight: '600', color: STATUS.bad.fg, lineHeight: 22}}>
                  {error}
                </CCText>
              </View>
            ) : null}
          </ScrollView>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: S[2],
              paddingHorizontal: S[6],
              paddingVertical: S[4],
              borderTopWidth: 1,
              borderTopColor: E.line,
            }}>
            <ListButton
              buttonRef={cancelRef}
              label="Cancel"
              onPress={cancel}
              disabled={busy}
              reduced={reduced}
            />
            <ListButton
              label={busy ? 'Deleting…' : deleteLabel(count)}
              icon="trash-outline"
              variant="danger"
              disabled={!canConfirm}
              onPress={confirm}
              a11yHint={typed && !exact ? 'Type DELETE in the field above first' : undefined}
              reduced={reduced}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}
